/**
 * Coach Learning Paths Dashboard API (M6.1)
 *
 * Read-side endpoints for the coach dashboard, backed by the unified
 * completion store (task_completion_records). All four endpoints live under
 * /api/private/coaching and are gated server-side to ROLE_COACH /
 * ROLE_SUPER_ADMIN; coaches are pinned to their active company and
 * companyIds[] params are honored for super admins only.
 *
 * Envelope: the backend ApiController wraps every payload in {data: ...},
 * so each call unwraps response.data.data.
 *
 * Known backend substitutions (documented in CoachLearningPathService):
 * - averageScore is always null; timeSpentMinutes / sessionCount come from
 *   the path_activity_events stream (active, tab-visible time only)
 * - estimatedCompletionDate carries the enrollment due date
 * - completedCourseIds / currentCourseId carry path_item ids (the same id
 *   space as PathCourseInfo.id); map via PathCourseInfo.courseId if an LMS
 *   course id is needed
 */
import axiosInstance from "@/api/axiosInstance";
import type {
  CoachLearningPathDetail,
  GetCoachPathsRequest,
  GetCoachPathsResponse,
  GetPathDetailRequest,
  GetCompanyPathProgressRequest,
  GetCompanyPathProgressResponse,
  LearnerEnrollmentInfo,
  PathItemAnalytics,
  ProgressStatus,
} from "@/modules/coaching/features/LearningPathsDashboard/types";

/**
 * Request to fetch learners across all paths
 */
export interface GetLearnersRequest {
  pathIds?: number[];
  companyIds?: number[];
  search?: string;
  status?: ProgressStatus[];
  page?: number;
  pageSize?: number;
}

/**
 * Response for learners list
 */
export interface GetLearnersResponse {
  learners: LearnerEnrollmentInfo[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/** Drop empty arrays so the query string only carries real filters. */
const nonEmpty = <T>(values?: T[]): T[] | undefined =>
  values !== undefined && values.length > 0 ? values : undefined;

/**
 * Fetches learning paths for the coach dashboard.
 *
 * GET /api/private/coaching/learning-paths
 *
 * @param params - Optional parameters for filtering, sorting, and pagination
 * @returns Promise with learning paths data and pagination info
 */
export const getCoachLearningPaths = async (
  params?: GetCoachPathsRequest,
): Promise<GetCoachPathsResponse> => {
  const response = await axiosInstance.get<{ data: GetCoachPathsResponse }>(
    "/api/private/coaching/learning-paths",
    {
      params: {
        search: params?.search,
        status: nonEmpty(params?.status),
        sortBy: params?.sortBy,
        sortDirection: params?.sortDirection,
        page: params?.page,
        pageSize: params?.pageSize,
        companyIds: nonEmpty(params?.companyIds),
      },
    },
  );
  return response.data.data;
};

/**
 * Fetches detailed information for a specific learning path
 * (summary + courses + milestones + enrollments + statistics + trends).
 *
 * GET /api/private/coaching/learning-paths/{pathId}
 *
 * Enrollment filters are flattened to top-level query params
 * (status[], companyIds[], search) per the backend contract.
 *
 * @param params - Parameters including pathId and options for what to include
 * @returns Promise with detailed path information
 */
export const getCoachPathDetail = async (
  params: GetPathDetailRequest,
): Promise<CoachLearningPathDetail> => {
  const response = await axiosInstance.get<{ data: CoachLearningPathDetail }>(
    `/api/private/coaching/learning-paths/${params.pathId}`,
    {
      params: {
        includeEnrollments: params.includeEnrollments,
        includeTrends: params.includeTrends,
        status: nonEmpty(params.enrollmentFilters?.status),
        companyIds: nonEmpty(params.enrollmentFilters?.companyIds),
        search: params.enrollmentFilters?.search,
      },
    },
  );
  return response.data.data;
};

/**
 * Fetches company-level learning path progress for the coach dashboard.
 *
 * GET /api/private/coaching/companies/learning-paths/progress
 *
 * Note: companies with zero active enrollments are omitted by the backend.
 *
 * @param params - Optional company scope (super admins only; coaches are
 *   pinned to their active company server-side)
 * @returns Promise with company progress data
 */
export const getCompanyPathProgress = async (
  params?: GetCompanyPathProgressRequest,
): Promise<GetCompanyPathProgressResponse> => {
  const response = await axiosInstance.get<{
    data: GetCompanyPathProgressResponse;
  }>("/api/private/coaching/companies/learning-paths/progress", {
    params: {
      companyIds: nonEmpty(params?.companyIds),
    },
  });
  return response.data.data;
};

/**
 * Fetches learner enrollment rows across learning paths.
 *
 * GET /api/private/coaching/learners
 *
 * @param params - Parameters for filtering learners
 * @returns Promise with learners data and pagination info
 */
export const getLearners = async (
  params?: GetLearnersRequest,
): Promise<GetLearnersResponse> => {
  const response = await axiosInstance.get<{ data: GetLearnersResponse }>(
    "/api/private/coaching/learners",
    {
      params: {
        pathIds: nonEmpty(params?.pathIds),
        companyIds: nonEmpty(params?.companyIds),
        search: params?.search,
        status: nonEmpty(params?.status),
        page: params?.page,
        pageSize: params?.pageSize,
      },
    },
  );
  return response.data.data;
};

/**
 * Per-item funnel / dwell / drop-off for a path (usage tracking).
 */
export const getCoachPathItemAnalytics = async (
  pathId: number,
): Promise<PathItemAnalytics> => {
  const response = await axiosInstance.get<{ data: PathItemAnalytics }>(
    `/api/private/coaching/learning-paths/${pathId}/item-analytics`,
  );
  return response.data.data;
};

export default getCoachLearningPaths;
